/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { QueryProps } from "../types/query";
import { getCallAvailability, getCallStatus, requeryCall } from "../services/callRequery";
import { getExtratoPollIntervalMs, getExtratoPollMaxTries } from "../services/extratoPdf";
import { pollUntil } from "../utils/asyncPolling";

export interface UseCallRequeryResult {
  canRequery: boolean;
  isRequerying: boolean;
  requery: () => void;
}

export function useCallRequery(
  query: QueryProps | undefined,
  onReconsultaFinished?: (freshQuery: QueryProps) => void
): UseCallRequeryResult {
  const [canRequery, setCanRequery] = useState(false);
  const [isRequerying, setIsRequerying] = useState(false);

  useEffect(() => {
    if (query?.status !== "5") {
      setCanRequery(false);
      return;
    }

    let cancelled = false;
    getCallAvailability(query.id)
      .then((products) => {
        if (cancelled) return;
        const eligible = products.some((product) =>
          product.apis.some((api) => api.requery_eligible)
        );
        setCanRequery(eligible);
      })
      .catch(() => {
        if (!cancelled) setCanRequery(false);
      });

    return () => {
      cancelled = true;
    };
  }, [query?.id, query?.status]);

  const onReconsultaFinishedRef = useRef(onReconsultaFinished);
  useEffect(() => {
    onReconsultaFinishedRef.current = onReconsultaFinished;
  });

  const statusPollCancelRef = useRef<(() => void) | null>(null);
  useEffect(() => {
    return () => {
      statusPollCancelRef.current?.();
      statusPollCancelRef.current = null;
    };
  }, [query?.id]);

  const requery = useCallback(() => {
    if (!query || isRequerying) return;
    setIsRequerying(true);
    requeryCall(query.id)
      .then(() => {
        toast.success("Reconsulta em andamento...");

        const { promise, cancel } = pollUntil<QueryProps>({
          fetch: () => getCallStatus(query.id),
          isDone: (freshQuery) => freshQuery.status !== "2",
          isError: () => false,
          intervalMs: getExtratoPollIntervalMs(),
          maxAttempts: getExtratoPollMaxTries(),
        });
        statusPollCancelRef.current = cancel;

        promise
          .then((freshQuery) => onReconsultaFinishedRef.current?.(freshQuery))
          .catch(() => {})
          .finally(() => setIsRequerying(false));
      })
      .catch(() => {
        toast.error("Não foi possível iniciar a reconsulta agora. Tente novamente.");
        setIsRequerying(false);
      });
  }, [query, isRequerying]);

  return { canRequery, isRequerying, requery };
}
